<template>
  <div>
    <b-overlay :show="isLoading">
      <div class="d-flex justify-content-between" :class="IS_MOBILE() ? 'flex-column' : 'align-items-center'">
        <component :is="IS_MOBILE() ? 'h5' : 'h3'">
          {{ $t('Change User Status for ') }} {{ currentProject }}
        </component>

        <b-form-group :class="IS_MOBILE() ? 'w-100' : 'w-50'">
          <b-form-input
            v-model="keyword"
            :placeholder="$t('Search Users')"
            @input="getAllUsersStatus(1)"
          />
        </b-form-group>
      </div>

      <b-table
        id="user-status-table"
        :items="users"
        :fields="[
          { key: 'name', label: $t('User') },
          { key: 'status', label: $t('Status') }
        ]"
        responsive
        show-empty
      >
        <template #head(name)="data">
          <div class="d-flex">
            <span class="text-capitalize">{{ data.label }}</span>
            <table-header-sort
              :sort-label="'NAME'"
              :sort-options="{ sortBy: sortBy, sortDirection: sortDirection }"
              @update:sort-options="options => { sortBy = options.sortBy; sortDirection = options.sortDirection; getAllUsersStatus(1); }"
            />
          </div>
        </template>

        <template #head()="data">
          <span class="text-capitalize">
            {{ data.label }}
          </span>
        </template> 

        <template #cell(name)="data">
          <user-with-tags
            :user="data.item"
            :name-class="!data.item.isActive ? 'text-muted' : ''"
          />
        </template>

        <template #cell(status)="data">
          <b-form-checkbox
            v-model="data.item.isActive"
            class="colorful-switch"
            switch
            @change="onStatusChange(data.item)"
          >
            <span class="switch-icon-left">
              <feather-icon
                icon="CheckIcon"
                color="#ffffff"
              />
            </span>
            <span
              class="switch-icon-right"
            >
              <feather-icon
                icon="XIcon"
                color="#ffffff"
              />
            </span>
          </b-form-checkbox>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="total > perPage"
        v-model="currentPage"
        :total-rows="total"
        :per-page="perPage"
        @change="getAllUsersStatus"
      />
    </b-overlay>

    <confirm-modal
      v-if="selectedUser"
      modal="change-user-status-modal"
      :title="selectedUser.isActive ? $t('Activate User') : $t('Deactivate User')"
      :description="selectedUser.isActive ? $t('Are you sure you want to activate this user in the project?') : $t('Are you sure you want to deactivate this user in the project?')"
      :ok-variant="selectedUser.isActive ? 'success' : 'danger'"
      :ok-title="selectedUser.isActive ? $t('Activate') : $t('Deactivate')"
      @confirm="changeUserStatusInProject"
      @cancel="selectedUser.isActive = !selectedUser.isActive"
      @hidden="selectedUser = null"
    />
  </div>
</template>

<script setup>
import useApollo from '@/plugins/graphql/useApollo';
import { BTable, BPagination, BFormInput, BFormGroup, BOverlay, BFormCheckbox } from 'bootstrap-vue';
import UserWithTags from '@/views/common/components/UserWithTags.vue';
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue';
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue';
import { getCurrentInstance, nextTick, onMounted, ref } from 'vue';
import store from '@/store';

const root = getCurrentInstance().proxy.$root;
const isLoading = ref(false)
const perPage = ref(10)
const currentPage = ref(1)
const total = ref(0)
const users = ref([])
const sortBy = ref('NAME')
const sortDirection = ref('ASC')
const keyword = ref('')
const selectedUser = ref(null)
const currentProject = store.state.project.project.name

const getAllUsersStatus = (page = null) => {
  isLoading.value = true
  if (page) currentPage.value = page

  useApollo.project.getAllUsersStatus({
    projectUid: store.state.project.selectedProject,
    first: perPage.value,
    page: currentPage.value,
    sortBy: sortBy.value,
    sortDirection: sortDirection.value,
    keyword: keyword.value
  }).then(response => {
    total.value = response.data.users.total
    users.value = response.data.users.data.map(({ projectRoles, ...user }) => ({
      ...user,
      isActive: !projectRoles?.data?.[0]?.deactivatedAt
    }))
  }).finally(() => {
    isLoading.value = false
  })
}

const onStatusChange = (user) => {
  selectedUser.value = user
  nextTick(() => {
    root.$bvModal.show('change-user-status-modal')
  })
}

const changeUserStatusInProject = () => {
  isLoading.value = true
  useApollo.users.changeUserStatusInProject({
    userUid: selectedUser.value.uuid,
    projectUid: store.state.project.selectedProject,
    enable: selectedUser.value.isActive,
  }).then(response => {
    this.showSuccessMessage({
      data: {
        message: response.data.changeUserStatusInProject.message,
      },
    })
  }).finally(() => { isLoading.value = false })
}

onMounted(() => {
  getAllUsersStatus()
})
</script>